import React, { Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { polyfillCSS } from "../../../utils/css";
import clsx from "clsx";
import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import {
  COACH_EXERCISE_LIBRARY_PUBLISHED_ROUTE,
  COACH_EXERCISE_LIBRARY_TAGS_ROUTE,
  COACH_PROGRAMS_FOLDERS_ROUTE,
} from "../../../routes/routes";
import {
  BodypartType,
  ExerciseAssetSort,
  MovementPatternType,
} from "@growth-machine-llc/stridist-api-client";
import { CoachExerciseLibraryHeaderFilters } from "../../../components/exercise-library/CoachExerciseLibraryHeaderFilters";
import {
  ALL_OPTIONS,
  ALL_OPTIONS_TYPE,
  ExerciseListFilterContext,
} from "../../../contexts/ExerciseLibraryFilterContext";
import { ExerciseAssetType } from "@growth-machine-llc/stridist-api-client";
import {
  allBodyparts,
  allBodypartsSelected,
  allMovementPatterns,
  allMovementPatternsSelected,
} from "../../../components/exercise-library/ExercisesTableScreen";
import { useQuery } from "@tanstack/react-query";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { useCoachTabsContext } from "../../../contexts/CoachTabsContext";
const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

export interface ExerciseLibraryFilters {
  bodyparts: BodypartType[] | ALL_OPTIONS_TYPE;
  movementPatterns: MovementPatternType[] | ALL_OPTIONS_TYPE;
  exerciseType: ExerciseAssetType;
  tagIds: number[] | null | ALL_OPTIONS_TYPE;
}

export const EXERCISE_LIBRARY_FILTERS_KEY = "exerciseLibraryFilters";

export const COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY =
  "coach-exercise-library-assets-count";

const CoachExerciseLibraryWrapper = () => {
  const location = useLocation();
  const s = useStyles();

  const defaultFilters = {
    query: "",
    sortKey: ExerciseAssetSort.NAME_ASC,
  };

  const defaultTableFilters: ExerciseLibraryFilters = {
    bodyparts: ALL_OPTIONS,
    movementPatterns: ALL_OPTIONS,
    exerciseType: ExerciseAssetType.ALL,
    tagIds: ALL_OPTIONS,
  };

  const { setExerciseAssetsCount } = useCoachTabsContext();

  const { data: exerciseAssetsCount } = useQuery({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY],
    queryFn: () => ExerciseAssetsService.getExerciseAssetsCount(),
  });

  React.useEffect(() => {
    exerciseAssetsCount && setExerciseAssetsCount(exerciseAssetsCount);
  }, [exerciseAssetsCount]);

  const [query, setQuery] = React.useState(defaultFilters.query);

  const [sortKey, setSortKey] = React.useState<ExerciseAssetSort>(
    defaultFilters.sortKey,
  );

  const [filters, setFilters] =
    React.useState<ExerciseLibraryFilters>(defaultTableFilters);

  const { bodyparts, movementPatterns, exerciseType, tagIds } = filters;

  const updateFilters = (newFilters: Partial<ExerciseLibraryFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handleQueryChange = React.useCallback(
    (query: string) => {
      setQuery(query);
    },
    [setQuery],
  );

  const handleSortKeyChange = React.useCallback(
    (sortKey: ExerciseAssetSort) => {
      setSortKey(sortKey);
    },
    [setSortKey],
  );

  const handleBodypartChange = React.useCallback(
    (bodyparts: BodypartType[]) => {
      updateFilters({
        bodyparts: allBodypartsSelected(bodyparts) ? ALL_OPTIONS : bodyparts,
      });
    },
    [updateFilters],
  );

  const handleMovementPatternChange = React.useCallback(
    (movementPatterns: MovementPatternType[]) => {
      updateFilters({
        movementPatterns: allMovementPatternsSelected(movementPatterns)
          ? ALL_OPTIONS
          : movementPatterns,
      });
    },
    [updateFilters],
  );

  const handleExerciseTypeChange = React.useCallback(
    (exerciseType: ExerciseAssetType) => {
      updateFilters({ exerciseType });
    },
    [updateFilters],
  );

  const handleTagIdsChange = React.useCallback(
    (tagIds?: number[], allOptionsSelected?: boolean) => {
      updateFilters({ tagIds: allOptionsSelected ? ALL_OPTIONS : tagIds });
    },
    [updateFilters],
  );

  React.useEffect(() => {
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [location.pathname]);

  const isTagsListRoute = location.pathname.includes(
    COACH_EXERCISE_LIBRARY_TAGS_ROUTE,
  );

  const filtersToReset =
    Object.values(filters).some((filter) => filter !== ALL_OPTIONS) ||
    query !== defaultFilters.query ||
    sortKey !== defaultFilters.sortKey;

  const handleResetFilters = React.useCallback(() => {
    updateFilters({
      bodyparts: ALL_OPTIONS,
      movementPatterns: ALL_OPTIONS,
      tagIds: ALL_OPTIONS,
      exerciseType: ExerciseAssetType.ALL,
    });
    setQuery(defaultFilters.query);
    setSortKey(defaultFilters.sortKey);
  }, [updateFilters]);

  const filterContextValue = {
    query,
    sortKey,
    bodyparts: bodyparts == ALL_OPTIONS ? allBodyparts : bodyparts,
    movementPatterns:
      movementPatterns == ALL_OPTIONS ? allMovementPatterns : movementPatterns,
    exerciseType,
    tagIds,
    handleQueryChange,
    handleSortKeyChange,
    handleBodypartChange,
    handleMovementPatternChange,
    handleTagIdsChange,
  };

  return (
    <ExerciseListFilterContext.Provider value={filterContextValue}>
      <Container className={clsx(s.root)} maxWidth="xl">
        {location.pathname !== COACH_PROGRAMS_FOLDERS_ROUTE && (
          <CoachExerciseLibraryHeaderFilters
            className={s.filters}
            query={query}
            isTagsRoute={isTagsListRoute}
            onChangeQuery={handleQueryChange}
            exerciseType={exerciseType}
            onChangeExerciseType={handleExerciseTypeChange}
            sortKey={sortKey}
            onChangeSortKey={handleSortKeyChange}
            disabledResetButton={!filtersToReset}
            showResetButton={true}
            onResetFilters={handleResetFilters}
            showFilterByType={
              location.pathname == COACH_EXERCISE_LIBRARY_PUBLISHED_ROUTE
            }
            defaultValues={{
              ...defaultFilters,
              exerciseType: defaultTableFilters.exerciseType,
            }}
          />
        )}
        <Suspense fallback={<PageSkeleton fullWidth />}>
          <Outlet />
        </Suspense>
      </Container>
    </ExerciseListFilterContext.Provider>
  );
};

export default CoachExerciseLibraryWrapper;
